import type { Feature, MultiPolygon, Polygon } from "geojson";

import { MAX_POI } from "@/constants/dataset";
import {
  DatasetData,
  DatasetDataRowV2,
  DatasetDataType
} from "@/types/dataset";
import { MarkerPinItem } from "@/types/map";
import {
  CatchmentDataset,
  CatchmentDatasetConfig,
  CatchmentTransport,
  ECatchmentTransport,
  ERoadExclusions
} from "@/types/spatial";

import { v4 as uuidv4 } from "uuid";

import { isOrdinalType } from "./dataset";
import { parseWKT } from "./wkt";

import { assignDenseRanks } from "./map/rank";

/**
 * Render gate for catchment datasets: returns the entries that should be DRAWN
 * on the map — those the user has left visible (`properties.isVisible`) AND that
 * fall within the catchment row limit.
 *
 * Rows are accumulated in `visualizationOrder`, counting EVERY staged dataset
 * (visible or not) so the budget matches the panel counter (`countTotalRows`).
 * Once the running total passes the limit, the remaining datasets are withheld
 * from the map even when `isVisible` is true — so the visibility (eye) toggle
 * can never force over-limit data to render. The dataset itself stays in the
 * config and the save/analysis flow; only its map rendering is suppressed.
 *
 * @param maxCatchments Limit from settings; falls back to `MAX_POI` (the same
 *   resolution the panel counter uses). A missing/non-positive value is treated
 *   as unlimited so a misconfigured limit never blanks the map.
 */
export function getRenderableCatchmentDatasets(
  datasets: CatchmentDatasetConfig["datasets"],
  visualizationOrder: string[] = [],
  maxCatchments?: number
): CatchmentDataset[] {
  const configuredLimit = maxCatchments || MAX_POI;
  const limit =
    Number.isFinite(configuredLimit) && configuredLimit > 0
      ? configuredLimit
      : Infinity;

  // Deterministic order for the cumulative walk: visualizationOrder first, then
  // any datasets not present in it (append, insertion order).
  const orderedIds = [
    ...visualizationOrder.filter((id) => datasets[id]),
    ...Object.keys(datasets).filter((id) => !visualizationOrder.includes(id))
  ];

  const renderable: CatchmentDataset[] = [];
  let runningRows = 0;
  for (const id of orderedIds) {
    const entry = datasets[id];
    const rows = entry?.rawDataset?.rows?.length ?? 0;
    const withinLimit = runningRows + rows <= limit;
    runningRows += rows;
    if (entry?.dataset?.properties?.isVisible && withinLimit) {
      renderable.push(entry);
    }
  }
  return renderable;
}

export function getRankingMap(
  rawDataset: DatasetData,
  labelAttributeName: string,
  rankConfig?: { maxRanking: number; isEnabled: boolean }
) {
  if (
    !rawDataset?.columns?.length ||
    !rawDataset?.rows?.length ||
    !rankConfig?.isEnabled
  )
    return {};

  const labelColumn = rawDataset.columns.find(
    (col) => col.name === labelAttributeName
  );

  if (!labelColumn || isOrdinalType(labelColumn.data_type)) {
    return {};
  }

  const labelIndex = rawDataset.columns.findIndex(
    (col) => col.name === labelAttributeName
  );

  const values = rawDataset.rows
    .map((row) => {
      const value = Number(row[labelIndex]);
      const label = `${row[labelIndex]}` || "";
      return { label, value };
    })
    .filter((v) => !Number.isNaN(v.value));

  const maxRanking = rankConfig?.maxRanking ?? 10;

  const rankMap: Record<string, Record<string, number>> = {
    [labelAttributeName]: {}
  };

  // Dense ranking, so rows sharing a value share a rank — see `assignDenseRanks`.
  for (const { item, rank } of assignDenseRanks(
    values,
    (v) => v.value,
    maxRanking
  )) {
    rankMap[labelAttributeName][item.label] = rank;
  }

  return rankMap;
}

function handleRow(
  row: DatasetDataRowV2,
  pointIndex: number,
  selectedAttributeIndex: number,
  index: number,
  callback: (
    long: number,
    lat: number,
    label: string,
    index: number,
    rank?: number
  ) => void,
  rankMap?: Record<string, Record<string, number>>,
  attributeName?: string
) {
  const pointWKT = row[pointIndex];
  if (typeof pointWKT !== "string") return;

  const geometry = parseWKT(pointWKT);
  if (!geometry) return;

  const label = `${row[selectedAttributeIndex]}` || `Marker #${index + 1}`;

  if (geometry?.type === "Point") {
    const [long, lat] = geometry.coordinates;

    const rank = attributeName ? rankMap?.[attributeName]?.[label] : undefined;
    callback(long, lat, label, index, rank);
  }
}

export function iterateRows(
  datasetValues: CatchmentDataset[],
  callback: (
    long: number,
    lat: number,
    label: string,
    index: number,
    rank?: number
  ) => void
) {
  for (const entry of datasetValues) {
    const { dataset, rawDataset } = entry;
    const attributeName = dataset?.selectedCategory?.attribute_name;

    const pointIndex = rawDataset.columns.findIndex(
      (v) => v.data_type === DatasetDataType.TYPE_GEOMETRY
    );

    if (pointIndex < 0) return;

    const selectedAttributeIndex = rawDataset.columns.findIndex(
      (v) => v.name === attributeName
    );

    const rankMap = getRankingMap(
      rawDataset,
      attributeName,
      dataset.style?.rankConfig
    );

    for (const [index, row] of rawDataset.rows.entries()) {
      handleRow(
        row,
        pointIndex,
        selectedAttributeIndex,
        index,
        callback,
        rankMap,
        attributeName
      );
    }
  }
}

export function createMarkersFromDataset(
  datasetValues: CatchmentDataset[],
  onFeature: (
    label: string,
    coordinates: [number, number],
    id: string,
    rank?: number
  ) => void
) {
  iterateRows(datasetValues, (long, lat, label, _, rank) => {
    const id = uuidv4();
    onFeature(label, [long, lat], id, rank);
  });
}

export const extractCoordinatesFromDatasets = (
  datasetValues: CatchmentDataset[]
): MarkerPinItem[] => {
  const coordinates: MarkerPinItem[] = [];

  iterateRows(datasetValues, (lng, lat) => {
    const id = uuidv4();
    coordinates.push({ id, lat, lng });
  });

  return coordinates;
};

/** Metres per degree of latitude, for the local flat-Earth projection.
 *  Longitude is scaled by `cos(lat)` since it shrinks toward the poles. */
const METERS_PER_DEG_LAT = 111_320;

/**
 * Area (m²) covered by equal-radius circles at `centers`, estimated by grid
 * rasterisation instead of a `turf.union` (which is too slow for many markers).
 * Overlapping cells are counted once. `cellSize = radius / 24` keeps it within
 * ~0.3% of the true union area. Returns 0 for no centres or a non-positive radius.
 */
export const rasterizedCircleArea = (
  centers: { lng: number; lat: number }[],
  radius: number
): number => {
  if (!centers.length || radius <= 0) return 0;

  const { lng: originLng, lat: originLat } = centers[0];
  const metersPerDegLng =
    METERS_PER_DEG_LAT * Math.cos((originLat * Math.PI) / 180);

  const cellSize = radius / 24;
  const radiusSq = radius * radius;
  const covered = new Set<string>();

  for (const center of centers) {
    // Project each centre into a local metre grid relative to the first centre.
    const cx = (center.lng - originLng) * metersPerDegLng;
    const cy = (center.lat - originLat) * METERS_PER_DEG_LAT;
    const minGx = Math.floor((cx - radius) / cellSize);
    const maxGx = Math.floor((cx + radius) / cellSize);
    const minGy = Math.floor((cy - radius) / cellSize);
    const maxGy = Math.floor((cy + radius) / cellSize);

    for (let gx = minGx; gx <= maxGx; gx++) {
      for (let gy = minGy; gy <= maxGy; gy++) {
        const dx = (gx + 0.5) * cellSize - cx;
        const dy = (gy + 0.5) * cellSize - cy;
        if (dx * dx + dy * dy <= radiusSq) {
          covered.add(`${gx},${gy}`);
        }
      }
    }
  }

  return covered.size * cellSize * cellSize;
};

/** Upper bound on grid cells so widely-varying polygon sizes can't blow up memory. */
const POLYGON_RASTER_CELL_CAP = 2_000_000;

type ProjectedRing = { points: number[][]; minY: number; maxY: number };

/** Outer ring of each polygon (holes ignored — isochrone contours are single rings). */
const collectOuterRings = (
  polygons: Feature<Polygon | MultiPolygon>[]
): number[][][] => {
  const rings: number[][][] = [];
  for (const feature of polygons) {
    const geometry = feature.geometry;
    if (geometry.type === "Polygon") {
      if (geometry.coordinates[0]?.length) rings.push(geometry.coordinates[0]);
    } else {
      for (const poly of geometry.coordinates) {
        if (poly[0]?.length) rings.push(poly[0]);
      }
    }
  }
  return rings;
};

/** Marks every grid cell whose centre falls inside `ring` via a scanline fill. */
const fillRingCells = (
  ring: ProjectedRing,
  cellSize: number,
  covered: Set<string>
): void => {
  const { points, minY, maxY } = ring;
  for (
    let gy = Math.floor(minY / cellSize);
    gy <= Math.floor(maxY / cellSize);
    gy++
  ) {
    const rowY = (gy + 0.5) * cellSize;
    // X where each edge crosses this scanline row.
    const crossings: number[] = [];
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const y1 = points[j][1];
      const y2 = points[i][1];
      if ((y1 <= rowY && rowY < y2) || (y2 <= rowY && rowY < y1)) {
        const t = (rowY - y1) / (y2 - y1);
        crossings.push(points[j][0] + t * (points[i][0] - points[j][0]));
      }
    }
    crossings.sort((a, b) => a - b);
    // Fill cells whose centre lies inside a [crossing, crossing] span.
    for (let k = 0; k + 1 < crossings.length; k += 2) {
      const gxStart = Math.ceil(crossings[k] / cellSize - 0.5);
      const gxEnd = Math.floor(crossings[k + 1] / cellSize - 0.5);
      for (let gx = gxStart; gx <= gxEnd; gx++) {
        covered.add(`${gx},${gy}`);
      }
    }
  }
};

/**
 * Area (m²) covered by `polygons` (e.g. isochrone contours), estimated by
 * scanline-rasterising their outer rings instead of a `turf.union` (too slow
 * for many complex contours). Overlapping cells are counted once. Accuracy is
 * ~0.3–0.5%. Returns 0 for an empty list.
 */
export const rasterizedPolygonArea = (
  polygons: Feature<Polygon | MultiPolygon>[]
): number => {
  const lonLatRings = collectOuterRings(polygons);
  if (!lonLatRings.length) return 0;

  const [originLng, originLat] = lonLatRings[0][0];
  const metersPerDegLng =
    METERS_PER_DEG_LAT * Math.cos((originLat * Math.PI) / 180);

  // Project rings into a local metre grid and track sizing for cellSize.
  let sumBBoxArea = 0;
  let minRingDim = Infinity;
  const rings: ProjectedRing[] = lonLatRings.map((ring) => {
    const points: number[][] = [];
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [lng, lat] of ring) {
      const x = (lng - originLng) * metersPerDegLng;
      const y = (lat - originLat) * METERS_PER_DEG_LAT;
      points.push([x, y]);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    sumBBoxArea += (maxX - minX) * (maxY - minY);
    minRingDim = Math.min(minRingDim, maxX - minX, maxY - minY);
    return { points, minY, maxY };
  });

  // Fine enough to resolve the smallest ring, floored so the total cell count
  // (and Set memory) stays bounded when ring sizes vary wildly.
  const cellSize = Math.max(
    minRingDim / 24,
    Math.sqrt(sumBBoxArea / POLYGON_RASTER_CELL_CAP)
  );
  if (!Number.isFinite(cellSize) || cellSize <= 0) return 0;

  const covered = new Set<string>();
  for (const ring of rings) {
    fillRingCells(ring, cellSize, covered);
  }

  return covered.size * cellSize * cellSize;
};

/**
 * Keep only the road exclusions valid for the current FF state.
 * Flag on  → drop the legacy "cycling" value.
 * Flag off → drop the new "motorway" value.
 */
export const filterRoadExclusionsByFlag = (
  exclusions: ERoadExclusions[] | undefined,
  isNewRoadExclusionEnabled: boolean
): ERoadExclusions[] => {
  const disabledValue = isNewRoadExclusionEnabled
    ? ERoadExclusions.CYCLING_PATH
    : ERoadExclusions.MOTORWAY;
  const validValues = Object.values(ERoadExclusions);
  return (exclusions ?? []).filter(
    (item) => item !== disabledValue && validValues.includes(item)
  );
};

/**
 * Exclusions to actually apply (API, save, summary). With the flag on they only
 * apply to Driving — dropped here for Walking/Cycling without erasing the stored
 * selection, so it's restored on return to Driving. Flag off: routing ignored.
 */
export const getEffectiveRoadExclusions = (
  exclusions: ERoadExclusions[] | undefined,
  routingType: CatchmentTransport | undefined,
  isNewRoadExclusionEnabled: boolean
): ERoadExclusions[] => {
  if (
    isNewRoadExclusionEnabled &&
    routingType !== ECatchmentTransport.DRIVING
  ) {
    return [];
  }
  return filterRoadExclusionsByFlag(exclusions, isNewRoadExclusionEnabled);
};
